import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadState } from "../../constants/enums";
import {
  IPbsQuoteHistoryFilters,
  IPbsQuoteModeState,
  initialPbsQuoteState,
} from "./pbsQuote.types";
import {
  calculatePbsQuote,
  createPbsQuote,
  createRenewalPbsQuote,
  fetchPbsQuoteHistory,
} from "./pbsQuoteThunk";

const resetModeState = (mode: IPbsQuoteModeState) => {
  mode.loading = LoadState.NotLoaded;
  mode.result = null;
  mode.errors = [];
  mode.error = null;
  mode.pfaBase64 = null;
  mode.lastRequestHash = null;
};

const pbsQuoteSlice = createSlice({
  name: "pbsQuote",
  initialState: initialPbsQuoteState,
  reducers: {
    clearCalculateResult: (state) => {
      resetModeState(state.calculate);
    },
    clearCreateResult: (state) => {
      resetModeState(state.create);
    },
    clearRenewalResult: (state) => {
      resetModeState(state.renewal);
    },
    markCalculateStale: (state, action: PayloadAction<string | undefined>) => {
      // Flag the summary as stale by clearing the stored hash so the UI can
      // compare against the current form values and prompt a recalculation.
      state.calculate.lastRequestHash = action.payload ?? null;
    },
    setHistoryFilters: (
      state,
      action: PayloadAction<Partial<IPbsQuoteHistoryFilters>>
    ) => {
      state.history.filters = {
        ...state.history.filters,
        ...action.payload,
      };
    },
    clearPbsQuoteState: () => initialPbsQuoteState,
  },
  extraReducers: (builder) => {
    // ---- Calculate ----
    builder
      .addCase(calculatePbsQuote.pending, (state) => {
        state.calculate.loading = LoadState.InProgress;
        state.calculate.errors = [];
        state.calculate.error = null;
      })
      .addCase(calculatePbsQuote.fulfilled, (state, action) => {
        state.calculate.loading = LoadState.Loaded;
        state.calculate.result = action.payload.data;
        state.calculate.errors = [];
        state.calculate.error = null;
        state.calculate.lastRequestHash = JSON.stringify(action.meta.arg);
      })
      .addCase(calculatePbsQuote.rejected, (state, action) => {
        state.calculate.loading = LoadState.Failed;
        state.calculate.errors = action.payload?.errors ?? [];
        state.calculate.error =
          action.payload?.status === 422
            ? null
            : action.payload?.message ?? "Something went wrong";
      });

    // ---- Create ----
    builder
      .addCase(createPbsQuote.pending, (state) => {
        state.create.loading = LoadState.InProgress;
        state.create.errors = [];
        state.create.error = null;
      })
      .addCase(createPbsQuote.fulfilled, (state, action) => {
        const { pfaBase64, ...result } = action.payload.data;
        state.create.loading = LoadState.Loaded;
        state.create.result = result;
        state.create.pfaBase64 = pfaBase64 ?? null;
        state.create.errors = [];
        state.create.error = null;
      })
      .addCase(createPbsQuote.rejected, (state, action) => {
        state.create.loading = LoadState.Failed;
        state.create.errors = action.payload?.errors ?? [];
        state.create.error =
          action.payload?.status === 422
            ? null
            : action.payload?.message ?? "Something went wrong";
      });

    // ---- Renewal ----
    builder
      .addCase(createRenewalPbsQuote.pending, (state) => {
        state.renewal.loading = LoadState.InProgress;
        state.renewal.errors = [];
        state.renewal.error = null;
      })
      .addCase(createRenewalPbsQuote.fulfilled, (state, action) => {
        const { pfaBase64, ...result } = action.payload.data;
        state.renewal.loading = LoadState.Loaded;
        state.renewal.result = result;
        state.renewal.pfaBase64 = pfaBase64 ?? null;
        state.renewal.errors = [];
        state.renewal.error = null;
      })
      .addCase(createRenewalPbsQuote.rejected, (state, action) => {
        state.renewal.loading = LoadState.Failed;
        state.renewal.errors = action.payload?.errors ?? [];
        state.renewal.error =
          action.payload?.status === 422
            ? null
            : action.payload?.message ?? "Something went wrong";
      });

    // ---- History ----
    builder
      .addCase(fetchPbsQuoteHistory.pending, (state) => {
        state.history.loading = LoadState.InProgress;
        state.history.error = null;
      })
      .addCase(fetchPbsQuoteHistory.fulfilled, (state, action) => {
        state.history.loading = LoadState.Loaded;
        state.history.list = action.payload.list;
        state.history.totalRecords = action.payload.totalRecords;
      })
      .addCase(fetchPbsQuoteHistory.rejected, (state, action) => {
        state.history.loading = LoadState.Failed;
        state.history.error = action.payload?.message ?? "Something went wrong";
      });
  },
});

export const {
  clearCalculateResult,
  clearCreateResult,
  clearRenewalResult,
  markCalculateStale,
  setHistoryFilters,
  clearPbsQuoteState,
} = pbsQuoteSlice.actions;

export const pbsQuoteReducer = pbsQuoteSlice.reducer;
